'use client'

import { useEffect, useRef, useState, type FormEvent } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { ArrowRight, Check, Loader2, X } from 'lucide-react'

import { checkUsername } from '@/lib/signup'
import { isValidHandle, normaliseHandle, HANDLE_MAX } from '@/components/auth/AuthFields'
import { CLAIM_COOKIE, CLAIM_COOKIE_MAX_AGE } from '@/lib/claim'
import { useT } from '@/components/i18n/LocaleProvider'

/**
 * Claim your handle, from the hero.
 *
 * Runs the same three gates the wizard's own handle step runs, in the same
 * order and against the same endpoint, so the answer here and the answer there
 * cannot disagree:
 *
 *   nothing typed  -> straight to /register, no fuss
 *   typed, invalid -> the format rule, so it can be fixed rather than guessed at
 *   typed, taken   -> said plainly here rather than three steps later
 *   typed, free    -> remembered in a cookie and carried into the wizard
 *
 * The cookie rather than a query param: a handle in the URL survives being
 * copied, shared and indexed, and this one rides along through an OAuth round
 * trip that rewrites the query string anyway. `lib/claim.ts` holds the name and
 * the lifetime, because the wizard has to read what this writes.
 *
 * `checkUsername` is unauthenticated, which is what makes any of this possible
 * from a page with no session.
 */

/** The host, shown as the prefix. Matches the one the wizard prints. */
const PAGE_HOST = (process.env.NEXT_PUBLIC_URL || 'https://fuse.mn')
  .replace(/\/$/, '')
  .replace(/^https?:\/\//, '')

/**
 * Names that cycle in the empty field.
 *
 * Real Mongolian given names, transliterated the way people write their own
 * handles here, so the field shows what a Fuse address looks like rather than
 * the word "yourname". Not localised: a handle is latin characters whatever
 * language the page is in, and these are names, not copy.
 */
const SAMPLE_NAMES = [
  'anu', 'temka', 'saraa', 'bilguun', 'nomin',
  'oyunaa', 'ganzo', 'uyanga', 'khulan', 'tuvshin',
]

const ROTATE_MS = 1000
/** Same debounce the wizard uses, so typing costs one request, not one per key. */
const DEBOUNCE_MS = 500

type State = 'idle' | 'invalid' | 'checking' | 'free' | 'taken'

const ClaimField = ({ cta, signIn }: { cta: string, signIn: string }) => {
  const t = useT()
  const router = useRouter()
  const [value, setValue] = useState('')
  const [state, setState] = useState<State>('idle')
  const [index, setIndex] = useState(0)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Guards against a slow answer for an old handle landing after a newer one. */
  const latest = useRef('')

  // Only while the field is empty. Ghost text moving under something being
  // typed is noise, and it stops for good once there is a value to read.
  useEffect(() => {
    if(value !== '') return
    const id = window.setInterval(() => {
      setIndex(current => (current + 1) % SAMPLE_NAMES.length)
    }, ROTATE_MS)
    return () => window.clearInterval(id)
  }, [value])

  useEffect(() => () => { if(timer.current) clearTimeout(timer.current) }, [])

  const runCheck = async (handle: string) => {
    const res = await checkUsername(handle)
    if(latest.current !== handle) return
    // A failed request is not a taken handle. Fall back to idle so the button
    // still works: the wizard checks again before anything is claimed.
    if(!res || res.error) return setState('idle')
    setState(res.data?.available ? 'free' : 'taken')
  }

  const change = (raw: string) => {
    const next = normaliseHandle(raw)
    setValue(next)
    latest.current = next
    if(timer.current) clearTimeout(timer.current)

    if(next === '') return setState('idle')
    if(!isValidHandle(next)) return setState('invalid')

    setState('checking')
    timer.current = setTimeout(() => { void runCheck(next) }, DEBOUNCE_MS)
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()

    // Only a name that was checked and found free is worth carrying over. An
    // invalid or taken one is left behind rather than seeding the wizard with
    // something it would immediately reject, and `checking` is treated the same
    // way: the answer has not arrived, so there is nothing to promise.
    if(state === 'free' && value !== '') {
      document.cookie = `${CLAIM_COOKIE}=${encodeURIComponent(value)}; Max-Age=${CLAIM_COOKIE_MAX_AGE}; Path=/; SameSite=Lax`
    }

    // Everyone reaches the wizard, whatever they typed. Blocking the button on
    // a taken name turns a small correction into a dead end on the first screen
    // of the site, and the wizard's handle step is built to sort it out.
    router.push('/register')
  }

  const message = {
    idle: null,
    checking: { text: t('auth.username.checking'), tone: 'text-muted-foreground' },
    invalid: { text: t('auth.username.rule'), tone: 'text-destructive' },
    taken: { text: t('auth.username.taken'), tone: 'text-destructive' },
    free: { text: t('auth.username.claimFree'), tone: 'text-emerald-600' },
  }[state]

  return (
    <form onSubmit={submit} className='w-full max-w-lg'>
      {/* One bordered row holding the prefix, the input and the button, so it
          reads as a single control. `focus-within` moves the ring to the row
          for the same reason, which is why the input itself has none. The
          border also carries the answer, so the state is visible before the
          message underneath is read. */}
      <div
        className={`flex items-center gap-1 rounded-full border-2 bg-card px-4 py-3 shadow-sm transition-colors ${
          state === 'free' ? 'border-emerald-500'
            : state === 'taken' || state === 'invalid' ? 'border-destructive'
              : 'border-border focus-within:border-orange-500'
        }`}
      >
        <span className='shrink-0 font-gilroy text-base font-semibold text-muted-foreground sm:text-lg'>
          {PAGE_HOST}/
        </span>

        {/* `border-0` as well as `outline-none`: without it the browser draws
            its own inset border and focus ring inside the row, which reads as a
            second box sitting inside the first. */}
        <input
          value={value}
          onChange={e => change(e.target.value)}
          autoCapitalize='none'
          autoCorrect='off'
          spellCheck={false}
          maxLength={HANDLE_MAX}
          placeholder={SAMPLE_NAMES[index]}
          aria-label={`${PAGE_HOST}/`}
          aria-invalid={state === 'invalid' || state === 'taken'}
          // `text-base` is 16px and is not negotiable on mobile: iOS Safari
          // zooms the page when a focused input is smaller than that, which
          // throws the whole layout sideways on the first tap. The prefix and
          // the button give up their space instead.
          className='w-0 min-w-0 flex-1 border-0 bg-transparent p-0 font-gilroy text-base font-semibold text-foreground shadow-none outline-none ring-0 placeholder:font-medium placeholder:text-muted-foreground/50 focus:border-0 focus:outline-none focus:ring-0 sm:text-lg'
        />

        {state !== 'idle' && (
          <span
            className={`flex size-6 shrink-0 items-center justify-center ${
              state === 'free' ? 'text-emerald-600'
                : state === 'checking' ? 'text-muted-foreground' : 'text-destructive'
            }`}
            aria-hidden='true'
          >
            {state === 'checking' && <Loader2 className='size-4 animate-spin' />}
            {state === 'free' && <Check className='size-4' />}
            {(state === 'taken' || state === 'invalid') && <X className='size-4' />}
          </span>
        )}

      </div>

      {/* Below the field and full width, rather than squeezed inside it. Sharing
          one row meant the prefix, the label and the input all competed for a
          phone's width and none could shrink, which pushed the page wider than
          the screen. Stacked, each gets the room it needs and the label stays.

          Never disabled. A person who has typed nothing is not making a
          mistake, they are just signing up the ordinary way, and a dead button
          under the first thing they see reads as the product being broken. The
          submit handler decides what each state does. */}
      <button
        type='submit'
        className='bg-gradient hover:bg-gradient-hover mt-2.5 flex w-full items-center justify-center gap-2 rounded-full px-5 py-3.5 font-gilroy text-base font-bold text-white transition-colors'
      >
        <span>{cta}</span>
        <ArrowRight className='size-4.5' aria-hidden='true' />
      </button>

      {/* Shown at every width. The persona pages' header carries a "back home"
          link rather than a sign-in one, so without this a returning visitor on
          /artist has no way in at all on desktop. On the home page it doubles
          the header's own link, which is the lesser problem: the header sits
          above the fold and this sits under the thing they came to press. */}
      <Link
        href='/login'
        className='mt-2 flex w-full items-center justify-center rounded-full px-5 py-3 font-gilroy text-base font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
      >
        {signIn}
      </Link>

      {/* `aria-live` so the answer is announced when it arrives, not only when
          somebody happens to move focus back to the field. */}
      <p className='mt-2 min-h-5 px-4 text-left text-xs font-medium' aria-live='polite'>
        {message && <span className={message.tone}>{message.text}</span>}
      </p>
    </form>
  )
}

export default ClaimField
